import React, { useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { Button } from '../components/ui/Button';
import { Mail, MessageCircle, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    queryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const queryTypes = [{
    value: '',
    label: 'Select a topic...'
  }, {
    value: 'courses',
    label: 'Course Enrollment & Questions'
  }, {
    value: 'technical',
    label: 'Technical Support'
  }, {
    value: 'accessibility',
    label: 'Accessibility Issue'
  }, {
    value: 'partnership',
    label: 'Partnership Inquiry'
  }, {
    value: 'press',
    label: 'Press & Media'
  }, {
    value: 'feedback',
    label: 'General Feedback'
  }, {
    value: 'other',
    label: 'Other'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        queryType: '',
        message: ''
      });
    }, 3000);
  };
  const contactMethods = [{
    icon: <Mail className="h-6 w-6" data-id="element-2032" />,
    title: 'Email',
    details: 'hello@regrowth.com',
    description: 'We respond within 2 business days',
    color: 'border-brand-magenta'
  }, {
    icon: <MessageCircle className="h-6 w-6" data-id="element-2033" />,
    title: 'Community',
    details: 'Join our Discord',
    description: 'Connect with our community',
    color: 'border-brand-cyan'
  }, {
    icon: <Phone className="h-6 w-6" data-id="element-2034" />,
    title: 'Office Hours',
    details: 'Tuesdays, 2-4pm PT',
    description: 'Drop-in virtual office hours',
    color: 'border-brand-orange'
  }, {
    icon: <MapPin className="h-6 w-6" data-id="element-2035" />,
    title: 'Location',
    details: 'Oakland, CA',
    description: 'Remote-first organization',
    color: 'border-brand-lime'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-2036">
      <HeroSection title="Get in Touch" subtitle="We'd love to hear from you. Send us a message and we'll respond as soon as possible." tag="Contact Us" bgColor="magenta" data-id="element-2037" />

      <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2038">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-2039">
          {/* Contact Methods - Left Column */}
          <div className="lg:col-span-1" data-id="element-2040">
            <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-2041">
              Other Ways to Reach Us
            </h2>

            <div className="space-y-6" data-id="element-2042">
              {contactMethods.map((method, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className={`border-l-4 ${method.color} bg-white p-6`} data-id="element-2043">
                  <div className="flex items-start gap-4" data-id="element-2044">
                    <div className="text-brand-black" data-id="element-2045">{method.icon}</div>
                    <div data-id="element-2046">
                      <h3 className="font-bold text-lg mb-1 text-brand-black" data-id="element-2047">
                        {method.title}
                      </h3>
                      <p className="text-brand-black font-medium mb-1" data-id="element-2048">
                        {method.details}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2049">
                        {method.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* FAQ Link */}
            <div className="mt-8 bg-brand-magenta/10 border-2 border-brand-magenta p-6" data-id="element-2050">
              <h3 className="font-bold text-lg mb-2 text-brand-black" data-id="element-2051">
                Looking for Quick Answers?
              </h3>
              <p className="text-gray-700 text-sm mb-4" data-id="element-2052">
                Check our FAQ page for answers to common questions about
                courses, enrollment, and policies.
              </p>
              <a href="/faq" className="text-brand-magenta font-bold text-sm hover:underline" data-id="element-2053">
                Visit FAQ →
              </a>
            </div>
          </div>

          {/* Contact Form - Right Column */}
          <div className="lg:col-span-2" data-id="element-2054">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white border-2 border-brand-black p-8 md:p-12" data-id="element-2055">
              <h2 className="text-3xl font-black uppercase mb-2 text-brand-black" data-id="element-2056">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-2057">
                Fill out the form below and we'll get back to you within 2
                business days.
              </p>

              {isSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="py-16 text-center" data-id="element-2058">
                  <div className="w-20 h-20 bg-brand-lime rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2059">
                    <CheckCircle className="h-10 w-10 text-white" data-id="element-2060" />
                  </div>
                  <h3 className="text-2xl font-black mb-3 text-brand-black" data-id="element-2061">
                    Message Sent!
                  </h3>
                  <p className="text-gray-700" data-id="element-2062">
                    Thanks for reaching out. We'll respond to your message soon.
                  </p>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2063">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2064">
                    <div data-id="element-2065">
                      <label htmlFor="firstName" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-2066">
                        First Name <span className="text-brand-magenta" data-id="element-2067">*</span>
                      </label>
                      <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="Jane" data-id="element-2068" />
                    </div>

                    <div data-id="element-2069">
                      <label htmlFor="lastName" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-2070">
                        Last Name <span className="text-brand-magenta" data-id="element-2071">*</span>
                      </label>
                      <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="Doe" data-id="element-2072" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2073">
                    <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-2074">
                      Email Address{' '}
                      <span className="text-brand-magenta" data-id="element-2075">*</span>
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="jane@example.com" data-id="element-2076" />
                  </div>

                  {/* Query Type Dropdown */}
                  <div data-id="element-2077">
                    <label htmlFor="queryType" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-2078">
                      What is this about?{' '}
                      <span className="text-brand-magenta" data-id="element-2079">*</span>
                    </label>
                    <select id="queryType" name="queryType" required value={formData.queryType} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors bg-white" data-id="element-2080">
                      {queryTypes.map(type => <option key={type.value} value={type.value} data-id="element-2081">
                          {type.label}
                        </option>)}
                    </select>
                  </div>

                  {/* Message */}
                  <div data-id="element-2082">
                    <label htmlFor="message" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-2083">
                      Message <span className="text-brand-magenta" data-id="element-2084">*</span>
                    </label>
                    <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows={6} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors resize-none" placeholder="Tell us what's on your mind..." data-id="element-2085" />
                    <p className="text-xs text-gray-500 mt-2" data-id="element-2086">
                      Please be as detailed as possible so we can help you
                      better.
                    </p>
                  </div>

                  {/* Privacy Notice */}
                  <div className="bg-gray-50 border-l-4 border-brand-magenta p-4" data-id="element-2087">
                    <p className="text-xs text-gray-600" data-id="element-2088">
                      By submitting this form, you agree to our{' '}
                      <a href="/policies/privacy" className="text-brand-magenta font-bold hover:underline" data-id="element-2089">
                        Privacy Policy
                      </a>
                      . We'll only use your information to respond to your
                      inquiry.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center justify-between pt-4" data-id="element-2090">
                    <p className="text-sm text-gray-600" data-id="element-2091">
                      <span className="text-brand-magenta" data-id="element-2092">*</span> Required
                      fields
                    </p>
                    <Button type="submit" color="magenta" size="lg" disabled={isSubmitting} className="min-w-[200px]" data-id="element-2093">
                      {isSubmitting ? <span className="flex items-center gap-2" data-id="element-2094">
                          <span className="animate-spin" data-id="element-2095">⏳</span>
                          Sending...
                        </span> : <span className="flex items-center gap-2" data-id="element-2096">
                          Send Message
                          <Send className="h-4 w-4" data-id="element-2097" />
                        </span>}
                    </Button>
                  </div>
                </form>}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Response Time Notice */}
      <section className="py-16 bg-brand-navy text-white" data-id="element-2098">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2099">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-2100">What to Expect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2101">
            <div data-id="element-2102">
              <div className="text-4xl font-black text-brand-magenta mb-2" data-id="element-2103">
                2
              </div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-2104">
                Business Days
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-2105">
                Average response time
              </p>
            </div>
            <div data-id="element-2106">
              <div className="text-4xl font-black text-brand-cyan mb-2" data-id="element-2107">24</div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-2108">
                Hours
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-2109">For urgent issues</p>
            </div>
            <div data-id="element-2110">
              <div className="text-4xl font-black text-brand-lime mb-2" data-id="element-2111">
                100%
              </div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-2112">
                Response Rate
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-2113">We reply to everyone</p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}